import React from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { MoneyInput } from '../../../components/MoneyInput';
import PhoneField from '../../../components/PhoneField';
import CountrySelect from '../../../components/CountrySelect';
import { SSNInput, US_STATES } from './_shared';
import useGeoCountry from '../../../hooks/useGeoCountry';

const OTHER_INCOME_TYPES = [
  { value: 'pension', label: 'Pension Income' },
  { value: 'royalties', label: 'Royalties' },
  { value: 'annuity', label: 'Annuity Income' },
  { value: 'alimony', label: 'Alimony' },
  { value: 'rental', label: 'Rental Property Income' },
  { value: 'part-time', label: 'Part-Time Employment' },
  { value: 'other', label: 'Other Income' },
  { value: 'irrevocable-trust', label: 'Other Irrevocable Trust Income' },
  { value: 'reverse-mortgage', label: 'Reverse Mortgage Proceeds' },
];

export default function EmploymentStep({ formData, updateFormData }) {
  const employers = formData.employers || [];
  const otherIncomes = formData.otherIncomes || [];
  const hasCoClient = formData.hasCoClient || false;
  const coClient = formData.coClient || {};
  const { countryCode: geoCountryCode } = useGeoCountry();
  const phoneDefault = geoCountryCode || 'US';

  const set = (patch) => updateFormData({ ...formData, ...patch });

  const addEmployer = () => set({ employers: [...employers, { employer: '', employmentIncome: '', totalIncome: '', liquidNetWorth: '' }] });
  const removeEmployer = (idx) => set({ employers: employers.filter((_, i) => i !== idx) });
  const updateEmployer = (idx, field, value) => set({ employers: employers.map((e, i) => (i === idx ? { ...e, [field]: value } : e)) });

  const addOtherIncome = () => set({ otherIncomes: [...otherIncomes, { type: '', description: '', amount: '' }] });
  const removeOtherIncome = (idx) => set({ otherIncomes: otherIncomes.filter((_, i) => i !== idx) });
  const updateOtherIncome = (idx, field, value) => set({ otherIncomes: otherIncomes.map((e, i) => (i === idx ? { ...e, [field]: value } : e)) });

  const toggleCoClient = () => set({ hasCoClient: !hasCoClient });
  const updateCoClient = (field, value) => set({ coClient: { ...coClient, [field]: value } });

  return (
    <div data-testid="employment-step">
      <h2 className="text-2xl font-bold text-[#0f172a] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Employment & Income</h2>

      {/* Employment */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium text-[#0f172a]">Employment <span className="text-red-600">*</span></h3>
          <Button onClick={addEmployer} size="sm" variant="outline" data-testid="add-employer-button">
            <span className="text-lg mr-1">+</span> Add
          </Button>
        </div>
        <p className="text-sm text-[#475569] mb-4">At least one employment entry with some information filled in is required to submit.</p>
        {employers.map((emp, idx) => (
          <div key={idx} className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0] mb-4">
            <div className="flex justify-between items-start mb-4">
              <span className="text-sm font-medium text-[#475569]">Employer #{idx + 1}</span>
              <Button onClick={() => removeEmployer(idx)} size="sm" variant="ghost" data-testid={`remove-employer-${idx}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Employer Name</Label><Input placeholder="Employer Name" value={emp.employer || ''} onChange={(e) => updateEmployer(idx, 'employer', e.target.value)} className="mt-2" /></div>
              <div><Label>Employment Income</Label><MoneyInput placeholder="$0" value={emp.employmentIncome} onChange={(v) => updateEmployer(idx, 'employmentIncome', v)} className="mt-2" /><p className="text-xs text-[#475569] mt-1">Annual amount</p></div>
              <div><Label>Total Income</Label><MoneyInput placeholder="$0" value={emp.totalIncome} onChange={(v) => updateEmployer(idx, 'totalIncome', v)} className="mt-2" /><p className="text-xs text-[#475569] mt-1">Annual amount</p></div>
              <div><Label>Liquid Net Worth</Label><MoneyInput placeholder="$0" value={emp.liquidNetWorth} onChange={(v) => updateEmployer(idx, 'liquidNetWorth', v)} className="mt-2" /></div>
            </div>
          </div>
        ))}
      </div>

      {/* Other Sources of Income */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-[#0f172a]">Other Sources of Income</h3>
          <Button onClick={addOtherIncome} size="sm" variant="outline" data-testid="add-other-income-button">
            <span className="text-lg mr-1">+</span> Add
          </Button>
        </div>
        {otherIncomes.map((item, idx) => (
          <div key={idx} className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0] mb-4">
            <div className="flex justify-between items-start mb-4">
              <span className="text-sm font-medium text-[#475569]">Income Source #{idx + 1}</span>
              <Button onClick={() => removeOtherIncome(idx)} size="sm" variant="ghost" data-testid={`remove-other-income-${idx}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={item.type || ''} onValueChange={(v) => updateOtherIncome(idx, 'type', v)}>
                  <SelectTrigger className="mt-2" data-testid={`other-income-type-${idx}`}><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {OTHER_INCOME_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Input placeholder="e.g., rental property address" value={item.description || ''} onChange={(e) => updateOtherIncome(idx, 'description', e.target.value)} className="mt-2" /></div>
              <div><Label>Annual Amount</Label><MoneyInput placeholder="$0" value={item.amount} onChange={(v) => updateOtherIncome(idx, 'amount', v)} className="mt-2" /></div>
            </div>
          </div>
        ))}
      </div>

      {/* Co-Client Toggle */}
      <div className="pt-6 border-t border-[#e2e8f0]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-[#0f172a]">Co-Client</h3>
          <Button onClick={toggleCoClient} size="sm" variant={hasCoClient ? "destructive" : "outline"} data-testid="toggle-coclient-button">
            {hasCoClient ? 'Remove Co-Client' : 'Add Co-Client'}
          </Button>
        </div>
        {hasCoClient && (
          <div className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input placeholder="First Name" value={coClient.firstName || ''} onChange={(e) => updateCoClient('firstName', e.target.value)} data-testid="coclient-first-name" />
              <Input placeholder="Middle Name" value={coClient.middleName || ''} onChange={(e) => updateCoClient('middleName', e.target.value)} />
              <Input placeholder="Last Name" value={coClient.lastName || ''} onChange={(e) => updateCoClient('lastName', e.target.value)} data-testid="coclient-last-name" />
              <Select value={coClient.gender || ''} onValueChange={(v) => updateCoClient('gender', v)}>
                <SelectTrigger><SelectValue placeholder="Gender" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <SSNInput id="coclient-ssn" placeholder="SSN (XXX-XX-XXXX)" value={coClient.ssn} onChange={(e) => updateCoClient('ssn', e.target.value)} label="co-client Social Security Number" testId="coclient-ssn-input" />
              <Select value={coClient.maritalStatus || ''} onValueChange={(v) => updateCoClient('maritalStatus', v)}>
                <SelectTrigger><SelectValue placeholder="Marital Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single</SelectItem><SelectItem value="married">Married</SelectItem><SelectItem value="divorced">Divorced</SelectItem><SelectItem value="widowed">Widowed</SelectItem>
                </SelectContent>
              </Select>
              <Input type="date" placeholder="Birthdate" value={coClient.birthdate || ''} onChange={(e) => updateCoClient('birthdate', e.target.value)} />
              <Input type="email" placeholder="Email" value={coClient.email || ''} onChange={(e) => updateCoClient('email', e.target.value)} />
              <PhoneField testId="coclient-mobile-phone" defaultCountry={phoneDefault} placeholder="Mobile Phone" value={coClient.mobilePhone} onChange={(v) => updateCoClient('mobilePhone', v)} />
              <PhoneField testId="coclient-home-phone" defaultCountry={phoneDefault} placeholder="Home Phone" value={coClient.homePhone} onChange={(v) => updateCoClient('homePhone', v)} />
              <PhoneField testId="coclient-work-phone" defaultCountry={phoneDefault} placeholder="Work Phone" value={coClient.workPhone} onChange={(v) => updateCoClient('workPhone', v)} />
              <Input placeholder="Fax" value={coClient.fax || ''} onChange={(e) => updateCoClient('fax', e.target.value)} />
            </div>

            {/* Co-Client Address */}
            {(() => {
              const ccCountry = coClient.country || 'United States';
              const ccIsUS = ccCountry === 'United States';
              return (
                <div className="mt-4 pt-4 border-t border-[#e2e8f0]">
                  <h4 className="text-sm font-medium text-[#0f172a] mb-3">Co-Client Address</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="coclient-country">Country</Label>
                      <div className="mt-2">
                        <CountrySelect
                          testId="coclient-country-trigger"
                          value={ccCountry}
                          onChange={(v) => set({ coClient: { ...coClient, country: v, state: '' } })}
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="coclient-address">Address (Line 1)</Label>
                      <Input id="coclient-address" className="mt-2" value={coClient.address || ''} onChange={(e) => updateCoClient('address', e.target.value)} data-testid="coclient-address" />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="coclient-address2">Address (Line 2)</Label>
                      <Input id="coclient-address2" className="mt-2" value={coClient.address2 || ''} onChange={(e) => updateCoClient('address2', e.target.value)} placeholder="Apt, suite, etc. (optional)" />
                    </div>
                    <div>
                      <Label htmlFor="coclient-city">City</Label>
                      <Input id="coclient-city" className="mt-2" value={coClient.city || ''} onChange={(e) => updateCoClient('city', e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="coclient-state">{ccIsUS ? 'State' : 'State / Province / Region'}</Label>
                      {ccIsUS ? (
                        <Select value={coClient.state || ''} onValueChange={(v) => updateCoClient('state', v)}>
                          <SelectTrigger className="mt-2" data-testid="coclient-state-trigger"><SelectValue placeholder="Select state" /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input id="coclient-state" className="mt-2" value={coClient.state || ''} onChange={(e) => updateCoClient('state', e.target.value)} placeholder="e.g. Ontario, Bavaria, NSW" data-testid="coclient-state-input" />
                      )}
                    </div>
                    <div>
                      <Label htmlFor="coclient-zip">{ccIsUS ? 'Zip Code' : 'Postal Code'}</Label>
                      <Input id="coclient-zip" className="mt-2" value={coClient.zip || ''} onChange={(e) => updateCoClient('zip', e.target.value)} />
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
